import { useMemo } from 'react';

const EARTH_RADIUS_M = 6371008.8;
const METERS_TO_FEET = 3.28084;
const DEG_TO_RAD = Math.PI / 180;

/**
 * Great-circle distance between two WGS84 points, in feet.
 * Pure JSON math — no google.maps / Turf dependency.
 *
 * @param {{ lat: number, lng: number }} a
 * @param {{ lat: number, lng: number }} b
 * @returns {number}
 */
export function distanceFeetBetween(a, b) {
	const lat1 = a.lat * DEG_TO_RAD;
	const lat2 = b.lat * DEG_TO_RAD;
	const dLat = (b.lat - a.lat) * DEG_TO_RAD;
	const dLng = (b.lng - a.lng) * DEG_TO_RAD;

	const sinDLat = Math.sin(dLat / 2);
	const sinDLng = Math.sin(dLng / 2);
	const h = sinDLat * sinDLat
		+ Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
	const meters = 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
	return meters * METERS_TO_FEET;
}

/**
 * True if any vertex is within maxFeet of point (legacy MapView semantics).
 *
 * @param {Array<{ lat: number, lng: number }>} coordinates
 * @param {{ lat: number, lng: number }} point
 * @param {number} maxFeet
 * @returns {boolean}
 */
export function isWithinVertexDistance(coordinates, point, maxFeet) {
	if (!coordinates || !coordinates.length || point == null || maxFeet == null) {
		return false;
	}
	for (let i = 0; i < coordinates.length; i++) {
		const c = coordinates[i];
		if (c == null || typeof c.lat !== 'number' || typeof c.lng !== 'number') {
			continue;
		}
		if (distanceFeetBetween(c, point) <= maxFeet) {
			return true;
		}
	}
	return false;
}

/**
 * Normalize MapView placemarkItems or GeoJSON-like features into
 * { title, coordinates: {lat,lng}[] } entries for proximity math.
 *
 * @param {Object|Array} featuresOrPlacemarks
 * @returns {Array<{ title: string, coordinates: Array<{lat:number,lng:number}> }>}
 */
export function normalizeProximityFeatures(featuresOrPlacemarks) {
	if (!featuresOrPlacemarks) {
		return [];
	}

	if (Array.isArray(featuresOrPlacemarks)) {
		return featuresOrPlacemarks.map((feature, index) => {
			if (feature && feature.geometry && feature.properties) {
				const title = feature.properties.title
					|| feature.properties.name
					|| `Feature ${index + 1}`;
				const coordinates = feature.properties.coordinatesLatLng
					|| geoJsonToLatLngs(feature.geometry)
					|| [];
				return { title, coordinates };
			}
			return {
				title: feature.title || feature.name || `Feature ${index + 1}`,
				coordinates: feature.coordinates || []
			};
		});
	}

	return Object.keys(featuresOrPlacemarks).map(key => {
		const item = featuresOrPlacemarks[key];
		return {
			title: item.title || key,
			coordinates: item.coordinates || []
		};
	});
}

function geoJsonToLatLngs(geometry) {
	if (!geometry || !geometry.type || !geometry.coordinates) {
		return [];
	}
	if (geometry.type === 'Point') {
		const [lng, lat] = geometry.coordinates;
		return [{ lat, lng }];
	}
	if (geometry.type === 'LineString') {
		return geometry.coordinates.map(([lng, lat]) => ({ lat, lng }));
	}
	if (geometry.type === 'Polygon') {
		const ring = geometry.coordinates[0] || [];
		return ring.map(([lng, lat]) => ({ lat, lng }));
	}
	return [];
}

/**
 * Compute which placemark titles are within distanceFeet of point.
 *
 * @param {Object|Array} featuresOrPlacemarks
 * @param {{ lat: number, lng: number }|null} point
 * @param {number|null|undefined} distanceFeet
 * @returns {{ visibleByTitle: Object<string, boolean>, hasAnyInProximity: boolean }}
 */
export function computeProximityVisibility(featuresOrPlacemarks, point, distanceFeet) {
	const visibleByTitle = {};
	let hasAnyInProximity = false;

	if (point == null || distanceFeet == null) {
		return { visibleByTitle, hasAnyInProximity };
	}

	const features = normalizeProximityFeatures(featuresOrPlacemarks);
	features.forEach(feature => {
		const visible = isWithinVertexDistance(feature.coordinates, point, distanceFeet);
		visibleByTitle[feature.title] = visible;
		if (visible) {
			hasAnyInProximity = true;
		}
	});

	return { visibleByTitle, hasAnyInProximity };
}

/**
 * React hook: proximity visibility from pure JSON inputs.
 * Does not call google.maps — callers apply setVisible / markers separately.
 *
 * @param {Object|Array} features
 * @param {{ lat: number, lng: number }|null} point
 * @param {number|null|undefined} distanceFeet
 */
export default function useProximity(features, point, distanceFeet) {
	return useMemo(
		() => computeProximityVisibility(features, point, distanceFeet),
		[features, point, distanceFeet]
	);
}
